import { useEffect, useState } from 'react'

import {
  DateValue,
  EAuctionBiddingEngine,
  EAuctionBiddingStatus,
  EAuctionStatus,
  EAuctionType,
  RegisteredForAuction,
} from '@bonhams1793/bonhams-typescript'

import { FireButtonProps } from '@nx/fire/button'
import { FireLink } from '@nx/fire/link'
import { canAuctionBeRegisteredFor, canLotBeBidOn } from '@nx/helpers'
import { getTranslate } from '@nx/translations'

import {
  StyledActionButton,
  StyledActionContainer,
  StyledBidNowHint,
  StyledLoginButton,
} from './ActionButtons.styles'
import translate from './ActionButtons.translate.json'

const { text, textHTML } = getTranslate(translate)

export function ActionButtons({
  showLoginModal,
  showRegisterModal,
  showBidModal,
  isLoggedIn,
  registerLink,
  userHasAnAddress,
  userRegistrations,
  lot,
  variant = 'ghost',
  isAddressInRegistrationModalEnabled = false,
  hideBiddingButtons = false,
  userHasBids,
}: ActionButtonsProps) {
  const [activeButton, setActiveButton] = useState<
    | 'LOGIN'
    | 'REGISTER_LINK'
    | 'REGISTER_MODAL'
    | 'REGISTRATION_PENDING'
    | 'BID_MODAL'
    | null
  >(null)

  useEffect(() => {
    const auctionCanBeRegisteredFor = canAuctionBeRegisteredFor({
      auctionType: lot.auctionType,
      auctionStatus: lot.auctionStatus,
      rowId: lot.auctionAMId,
      biddingStatus: lot.biddingStatus,
      biddingEngine: lot.biddingEngine,
    })

    if (!auctionCanBeRegisteredFor) {
      setActiveButton(null)
      return
    }

    if (!isLoggedIn) {
      setActiveButton('LOGIN')
      return
    }

    const registration = userRegistrations.find(
      (registration) => String(registration.iSaleNo) === String(lot.auctionId)
    )

    const userRegisteredToBid = !!registration
    const userApprovedToBid = !!registration?.approvedToBid

    if (
      !userRegisteredToBid &&
      (isAddressInRegistrationModalEnabled ||
        typeof userHasAnAddress !== 'undefined')
    ) {
      setActiveButton(
        isAddressInRegistrationModalEnabled || userHasAnAddress
          ? 'REGISTER_MODAL'
          : registerLink
            ? 'REGISTER_LINK'
            : null
      )
      return
    }

    const lotCanBeBidOn = canLotBeBidOn({
      auctionType: lot.auctionType,
      biddableFrom: lot.biddableFrom,
      biddingEngine: lot.biddingEngine,
      isExhibition: lot.isExhibition,
      isPreview: lot.isPreview,
      isToBeSold: lot.isToBeSold,
      lotAMId: lot.lotAMId,
    })

    if (lotCanBeBidOn && userRegisteredToBid) {
      if (hideBiddingButtons) {
        setActiveButton(null)
        return
      }

      setActiveButton(
        userApprovedToBid
          ? showBidModal
            ? 'BID_MODAL'
            : null
          : 'REGISTRATION_PENDING'
      )
      return
    }

    setActiveButton(null)
  }, [
    isLoggedIn,
    lot,
    userHasAnAddress,
    userRegistrations,
    isAddressInRegistrationModalEnabled,
    registerLink,
    showBidModal,
    hideBiddingButtons,
  ])

  switch (activeButton) {
    case 'LOGIN':
      return (
        <StyledLoginButton variant={variant} onClick={showLoginModal}>
          {text('loginToBid')}
        </StyledLoginButton>
      )
    case 'REGISTER_MODAL':
      return (
        <StyledActionContainer>
          <StyledActionButton variant={variant} onClick={showRegisterModal}>
            {text('registerNow')}
          </StyledActionButton>
        </StyledActionContainer>
      )
    case 'REGISTER_LINK':
      return (
        <StyledActionContainer>
          <StyledActionButton variant={variant} href={registerLink}>
            {text('register')}
          </StyledActionButton>
        </StyledActionContainer>
      )
    case 'REGISTRATION_PENDING':
      return (
        <StyledBidNowHint>
          {textHTML('registrationPending', {
            link: (
              <FireLink
                key="clientServicesLink"
                href="/stories/30262/client-services/"
              >
                {text('clientServicesLink')}
              </FireLink>
            ),
          })}
        </StyledBidNowHint>
      )
    case 'BID_MODAL':
      return (
        <StyledActionContainer>
          <StyledActionButton variant={variant} onClick={showBidModal}>
            {userHasBids ? text('increaseYourBid') : text('placeBid')}
          </StyledActionButton>
        </StyledActionContainer>
      )
    default:
      return null
  }
}

export interface ActionButtonsProps {
  showLoginModal: () => void
  showRegisterModal: () => void
  showBidModal?: () => void
  isLoggedIn: boolean
  registerLink?: string
  userHasAnAddress?: boolean
  userRegistrations: RegisteredForAuction[]
  userHasBids?: boolean
  lot: {
    auctionAMId?: string
    auctionId: string
    auctionStatus: EAuctionStatus
    auctionType: EAuctionType
    biddableFrom?: Omit<DateValue, 'timestamp'> | null
    biddingEngine: EAuctionBiddingEngine | null
    biddingStatus: EAuctionBiddingStatus
    isExhibition: boolean
    isPreview: boolean
    isToBeSold: boolean
    lotAMId?: string
  }
  variant?: FireButtonProps['variant']
  isAddressInRegistrationModalEnabled?: boolean
  hideBiddingButtons?: boolean
}
